<script lang="ts">
  import { _, locale } from "svelte-i18n";

  import InputField from "$lib/components/InputField/InputField.svelte";
  import RadioGroup from "$lib/components/RadioGroup/RadioGroup.svelte";
  import Typography from "$lib/components/Typography/Typography.svelte";
  import Icon from "$lib/components/Icon/Icon.svelte";
  import { subscribeToNewsletter } from "$lib/utils/subscribeToNewsletter";
  import { linkTo } from "$lib/utils/url";
  import { modals } from "$lib/stores/modal";
  import Button from "$lib/components/Button/Button.svelte";
  import { type DOB } from "$lib/utils/dates/dates";
  import DobInput from "../DobInput/DobInput.svelte";

  let email = "";
  let gender = "undisclosed";
  let dob: DOB = { day: "", month: "", year: "" };
  let honeypot = "";
  export let emailError = false;
  export let emailErrorMessage = "";
  export let signupSuccess = false;

  export let dark = false;

  const handleSubmit = async () => {
    let lang = $locale;
    lang = lang && lang.split("-")[0];

    const res = await subscribeToNewsletter(
      email,
      gender,
      honeypot,
      lang,
      `${dob.day ? `${dob.day}/` : ""}${dob.month || ""}/${dob.year}`,
    );

    emailError = res.emailError;
    signupSuccess = res.signupSuccess;
    emailErrorMessage = $_(res.errorMessage || "");
  };
</script>

<style lang="scss">
  .newsletter-signup {
    .inline-anchor {
      cursor: pointer;
      text-decoration: none;
      &:hover {
        color: $inverse-on-surface-60;
      }
    }
    &--blurb {
      padding: 24px 0;
    }

    &--email-wrapper {
      margin-bottom: 24px;
    }

    &--check-wrapper {
      position: absolute;
      left: -9999px;
      visibility: hidden;
    }

    &--gender {
      display: flex;
      align-items: flex-start;
      flex-wrap: wrap;
      margin-top: 22px;
      gap: 24px;
    }

    &--success-copy {
      margin-top: 24px;
      display: flex;
      align-items: center;

      &__text {
        margin-left: 8px;
      }
    }
  }

  .newsletter-signup {
    &--submit-wrapper {
      margin-top: 24px;
    }
  }

  @include breakpoint(md) {
    .newsletter-signup {
      &--email-wrapper {
        flex-grow: 1;
        margin-bottom: 0;
      }

      &--submit-wrapper {
        flex-grow: 1;
        margin-top: 24px;
        margin-bottom: 0;
      }
    }
  }
</style>

<div class="newsletter-signup">
  <Typography component="h2" variant="h2" theme="inverse-on-surface">
    {$_("newsletter-signup.title")}
  </Typography>
  {#if !signupSuccess}
    <div class="newsletter-signup--blurb">
      <Typography component="p" variant="caption2" theme="inverse-on-surface">
        {$_("newsletter-signup.blurb")}
      </Typography>
    </div>
    <form
      class="newsletter-signup--form"
      on:submit|preventDefault={handleSubmit}
    >
      <div class="newsletter-signup--check-wrapper">
        <InputField
          type="text"
          id="newsletter-name"
          name="name"
          bind:value={honeypot}
          fullWidth={true}
          tabindex={-1}
          autocomplete="off"
        />
      </div>
      <div class="newsletter-signup--email-wrapper">
        <InputField
          type="email"
          name="emailAddress"
          placeholder={$_("Email address")}
          bind:value={email}
          error={emailError}
          errorMessage={emailErrorMessage}
          fullWidth={true}
          inlineSubmitBtn={false}
        />
      </div>
      <DobInput bind:dob />
      <div
        class="newsletter-signup--gender"
        style={emailError && "margin-top: 0px"}
      >
        <RadioGroup
          items={[
            { label: $_("men"), name: "men" },
            { label: $_("women"), name: "women" },
          ]}
          bind:group={gender}
          margin="0"
          light={!dark}
        />
        <Typography
          component="p"
          variant="body1"
          theme={dark ? "on-surface" : "inverse-on-surface"}
        >
          <a
            href={linkTo("legal/privacy-policy")}
            on:click|preventDefault={() => {
              modals.open("privacyPolicy");
            }}
            class="inline-anchor"
          >
            {$_("Privacy Policy")}</a
          >
        </Typography>
      </div>
      <div class="newsletter-signup--submit-wrapper">
        <div>
          <Button
            label={$_("Submit")}
            primary={true}
            primaryInvert={true}
            fullWidth={true}
            theme="on-surface"
            hoverTheme="inverse-on-surface"
            submit={true}
          />
        </div>
      </div>
    </form>
  {:else}
    <div class="newsletter-signup--success-copy">
      <Icon icon="done" color="var(--inverse-on-surface)" />
      <div class="newsletter-signup--success-copy__text">
        <Typography component="p" variant="body1" theme="inverse-on-surface">
          {$_("newsletter-signup.success")}
        </Typography>
      </div>
    </div>
  {/if}
</div>
